import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
    ClockIcon,
    XMarkIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    PlusIcon
} from '@heroicons/react/24/outline';
import { scheduledOrdersApi } from '../utils/advancedOrdersApi';
import { showSuccessNotification, showErrorNotification } from './NotificationManager';

const ScheduledOrdersManager = ({ }) => {
    const navigate = useNavigate();
    const [scheduledOrders, setScheduledOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [actionLoading, setActionLoading] = useState({});

    useEffect(() => {
        fetchScheduledOrders();
    }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchScheduledOrders = async () => {
        setLoading(true);
        try {
            const params = filter !== 'all' ? { status: filter } : {};
            const response = await scheduledOrdersApi.getScheduledOrders(params);

            if (response.success) {
                setScheduledOrders(response.data || []);
            } else {
                showErrorNotification('Failed to load scheduled orders');
            }
        } catch (error) {
            console.error('Failed to fetch scheduled orders:', error);
            showErrorNotification('Failed to load scheduled orders');
        } finally {
            setLoading(false);
        }
    };

    const cancelScheduledOrder = async (orderId) => {
        if (!window.confirm('Are you sure you want to cancel this scheduled order? You will be refunded for unused executions.')) {
            return;
        }

        setActionLoading(prev => ({ ...prev, [orderId]: 'cancelling' }));

        try {
            const response = await scheduledOrdersApi.cancelScheduledOrder(orderId);

            if (response.success) {
                showSuccessNotification('Scheduled order cancelled successfully');
                fetchScheduledOrders();
            } else {
                showErrorNotification(response.message || 'Failed to cancel scheduled order');
            }
        } catch (error) {
            showErrorNotification('Failed to cancel scheduled order');
        } finally {
            setActionLoading(prev => ({ ...prev, [orderId]: null }));
        }
    };

    const formatRepeatType = (order) => {
        if (order.repeatType === 'none') return 'One-time';
        const days = order.repeatDays?.join(', ') || '';
        return `${order.repeatType.charAt(0).toUpperCase() + order.repeatType.slice(1)} (${days})`;
    };

    const getNextExecution = (order) => {
        if (!order.nextExecution) return 'N/A';
        return new Date(order.nextExecution).toLocaleString();
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'scheduled':
                return <ClockIcon className="w-5 h-5 text-cheese" />;
            case 'completed':
                return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
            case 'cancelled':
                return <XMarkIcon className="w-5 h-5 text-red-500" />;
            case 'failed':
                return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
            default:
                return <ClockIcon className="w-5 h-5 text-gray-400" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'scheduled':
                return 'bg-secondary-100 text-secondary-800 border-secondary-200';
            case 'completed':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'cancelled':
                return 'bg-red-100 text-red-800 border-red-200';
            case 'failed':
                return 'bg-red-100 text-red-800 border-red-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pepperoni"></div>
                <span className="ml-3 text-olive-light font-sans">Loading scheduled orders...</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-crust font-display">Scheduled Orders</h2>
                    <p className="text-olive-light font-sans">Manage your scheduled and recurring orders</p>
                </div>
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate('/user/create-scheduled-order')}
                    className="w-full sm:w-auto bg-gradient-to-r from-pepperoni to-primary-800 hover:from-primary-800 hover:to-pepperoni text-accentwhite px-6 py-3 rounded-xl font-bold font-display shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                >
                    <PlusIcon className="w-5 h-5" />
                    Schedule Order
                </motion.button>
            </div>

            {/* Filter Tabs */}
            <div className="bg-accentwhite rounded-2xl shadow-card border border-secondary-200 p-2 overflow-x-auto">
                <div className="flex space-x-1 min-w-[340px] sm:min-w-0">
                    {[
                        { id: 'all', label: 'All' },
                        { id: 'scheduled', label: 'Active' },
                        { id: 'completed', label: 'Completed' },
                        { id: 'cancelled', label: 'Cancelled' }
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setFilter(tab.id)}
                            className={`flex-1 py-3 px-4 rounded-xl font-bold font-display text-sm transition-all duration-300 whitespace-nowrap ${filter === tab.id
                                ? 'bg-gradient-to-r from-cheese to-secondary-500 text-crust shadow-lg'
                                : 'text-olive-light hover:text-crust hover:bg-secondary-50'
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Orders List */}
            <AnimatePresence>
                {scheduledOrders.length === 0 ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-accentwhite rounded-2xl shadow-card border border-secondary-200 p-12 text-center"
                    >
                        <ClockIcon className="w-16 h-16 text-secondary-300 mx-auto mb-4" />
                        <h3 className="text-xl font-bold text-crust font-display mb-2">
                            No scheduled orders found
                        </h3>
                        <p className="text-olive-light font-sans mb-6">
                            Create a scheduled order to see it here!
                        </p>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => navigate('/user/create-scheduled-order')}
                            className="bg-pepperoni text-accentwhite px-6 py-3 rounded-xl font-bold font-display shadow-lg hover:shadow-xl transition-all duration-300"
                        >
                            Create Scheduled Order
                        </motion.button>
                    </motion.div>
                ) : (
                    <div className="space-y-4">
                        {scheduledOrders.map((order) => (
                            <motion.div
                                key={order._id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-accentwhite rounded-2xl shadow-card border border-secondary-200 p-6 hover:shadow-lg transition-all duration-300"
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        {getStatusIcon(order.status)}
                                        <div>
                                            <h3 className="text-lg font-bold text-crust font-display">
                                                {order.restaurant?.name || 'Restaurant'}
                                            </h3>
                                            <p className="text-sm text-olive-light font-sans">
                                                Order ID: {order.customId}
                                            </p>
                                        </div>
                                    </div>
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold font-display border ${getStatusColor(order.status)}`}>
                                        {order.status.toUpperCase()}
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                                    <div className="bg-secondary-50 rounded-xl p-4 flex flex-col items-start">
                                        <div className="text-xs text-olive-light font-sans mb-1">Next Execution</div>
                                        <div className="font-bold text-crust font-display break-words">{getNextExecution(order)}</div>
                                    </div>
                                    <div className="bg-secondary-50 rounded-xl p-4 flex flex-col items-start">
                                        <div className="text-xs text-olive-light font-sans mb-1">Repeat Pattern</div>
                                        <div className="font-bold text-crust font-display break-words">{formatRepeatType(order)}</div>
                                    </div>
                                    <div className="bg-secondary-50 rounded-xl p-4 flex flex-col items-start">
                                        <div className="text-xs text-olive-light font-sans mb-1">Total Paid</div>
                                        <div className="font-bold text-crust font-display break-words">₦{order.totalPrice}</div>
                                    </div>
                                    <div className="bg-secondary-50 rounded-xl p-4 flex flex-col items-start">
                                        <div className="text-xs text-olive-light font-sans mb-1">Executions</div>
                                        <div className="font-bold text-crust font-display break-words">
                                            {order.executedOrders?.length || 0} completed
                                        </div>
                                    </div>
                                </div>

                                {/* Meals */}
                                <div className="mb-4">
                                    <h4 className="text-sm font-bold text-crust font-display mb-2">Meals:</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {order.meals?.map((meal, index) => (
                                            <span
                                                key={index}
                                                className="bg-gray-100 text-gray-700 px-3 py-1 rounded-lg text-sm font-sans"
                                            >
                                                {meal.meal?.name || 'Meal'} x{meal.quantity}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                {/* Execution History */}
                                {order.executedOrders && order.executedOrders.length > 0 && (
                                    <div className="mb-4">
                                        <h4 className="text-sm font-bold text-crust font-display mb-2">Recent Executions:</h4>
                                        <div className="space-y-2">
                                            {order.executedOrders.slice(-3).map((execution, index) => (
                                                <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                                                    <span className="text-sm font-sans text-olive-light">
                                                        {new Date(execution.executedAt).toLocaleDateString()}
                                                    </span>
                                                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${execution.status === 'success'
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-red-100 text-red-800'
                                                        }`}>
                                                        {execution.status.toUpperCase()}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Actions */}
                                {order.status === 'scheduled' && (
                                    <div className="flex gap-3">
                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={() => cancelScheduledOrder(order._id)}
                                            disabled={actionLoading[order._id]}
                                            className="bg-gradient-to-r from-primary-600 to-pepperoni hover:from-pepperoni hover:to-primary-600 text-accentwhite px-4 py-2 rounded-xl font-bold font-display text-sm shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 flex items-center gap-2"
                                        >
                                            {actionLoading[order._id] === 'cancelling' ? (
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accentwhite"></div>
                                            ) : (
                                                <XMarkIcon className="w-4 h-4" />
                                            )}
                                            Cancel Schedule
                                        </motion.button>
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </div>
                )}
            </AnimatePresence>


        </div>
    );
};

export default ScheduledOrdersManager;